import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Numbers } from '@app/constants';
import { ToastTypeEnum } from '@app/enums/toast-type.enum';
import { SiteDataCleanerService, VERSION_KEY } from '@app/services/site-data-cleaner.service';
import { ToastService } from '@app/services/toast.service';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

interface VersionInfo {
  version: string;
  buildHash: string;
  buildDate: string;
}

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_PATH = '/assets/version.json';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService implements OnDestroy {
  private currentBuildHash: string | null = null;
  private subscription: Subscription | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly toastService: ToastService,
    private readonly siteDataCleaner: SiteDataCleanerService
  ) {}

  startChecking(): void {
    if (this.subscription) {
      return;
    }

    this.fetchVersion().subscribe((versionInfo) => {
      if (versionInfo) {
        this.currentBuildHash = versionInfo.buildHash;
      }
    });

    this.subscription = timer(CHECK_INTERVAL_MS, CHECK_INTERVAL_MS)
      .pipe(
        switchMap(() => this.fetchVersion()),
        catchError(() => EMPTY)
      )
      .subscribe((versionInfo) => {
        if (!versionInfo || !this.currentBuildHash) {
          return;
        }

        if (versionInfo.buildHash !== this.currentBuildHash) {
          this.notifyNewVersion(versionInfo.buildHash);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /** Muestra el banner. Llamado tanto por el polling como por el APP_INITIALIZER. */
  notifyNewVersion(newHash: string): void {
    this.toastService.open({
      type: ToastTypeEnum.Exclaim,
      title: 'Nueva versión disponible',
      description: 'Hay una actualización lista. Se limpiarán los datos temporales del sitio.',
      closeBtnIsActive: true,
      timer: Numbers.zero,
      button: {
        label: 'Actualizar ahora',
        callback: () => this.applyUpdate(newHash),
      },
    });
  }

  /** Solo se ejecuta cuando el usuario hace clic en "Actualizar ahora" */
  private async applyUpdate(newHash: string): Promise<void> {
    await this.siteDataCleaner.clearAllSiteData();
    localStorage.setItem(VERSION_KEY, newHash);
    this.reloadPage();
  }

  /** Recarga la página. Extraído para facilitar testing. */
  private reloadPage(): void {
    window.location.reload();
  }

  fetchVersion() {
    return this.http
      .get<VersionInfo>(`${VERSION_PATH}?t=${Date.now()}`)
      .pipe(catchError(() => EMPTY));
  }
}
